import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = {
  contact_id: '',
  due_date: '',
  period_start: '',
  period_end: '',
  line_items: [{ description: '', quantity: 1, price: '' }],
  vat_amount: '',
  payment_method: '',
  payment_type: '',
  payment_ref: '',
  status: 'unpaid',
  paid_date: '',
};

export const BillFormDialog = ({ open, onOpenChange, bill, onSaved }) => {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchContacts();
    if (bill) {
      setForm({
        contact_id: bill.contact_id || '',
        due_date: bill.due_date || '',
        period_start: bill.period_start || '',
        period_end: bill.period_end || '',
        line_items: bill.line_items?.length ? bill.line_items : emptyForm.line_items,
        vat_amount: bill.vat_amount || '',
        payment_method: bill.payment_method || '',
        payment_type: bill.payment_type || '',
        payment_ref: bill.payment_ref || '',
        status: bill.status || 'unpaid',
        paid_date: bill.paid_at ? bill.paid_at.slice(0, 10) : '',
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, bill]);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/bill-contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(res.data || []);
    } catch (e) {
      console.error('Failed to fetch contacts:', e);
    }
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, line_items: items });
  };

  const addItem = () => setForm({ ...form, line_items: [...form.line_items, { description: '', quantity: 1, price: '' }] });
  const removeItem = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const subtotal = form.line_items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.price) || 0), 0);
  const total = subtotal + (parseFloat(form.vat_amount) || 0);

  const handleSave = async () => {
    if (!form.contact_id) return toast.error('Select a contact');
    if (form.line_items.some((i) => !i.description.trim())) return toast.error('Each line item needs a description');
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...form,
        vat_amount: parseFloat(form.vat_amount) || 0,
        line_items: form.line_items.map((i) => ({
          description: i.description,
          quantity: parseFloat(i.quantity) || 1,
          price: parseFloat(i.price) || 0,
        })),
        due_date: form.due_date || null,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        payment_method: form.payment_method || null,
        payment_type: form.payment_type || null,
        payment_ref: form.payment_ref || null,
        paid_date: form.status === 'paid' && form.paid_date ? form.paid_date : null,
      };
      if (bill) {
        await axios.put(`${API_URL}/bills/${bill.bill_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Bill updated');
      } else {
        const res = await axios.post(`${API_URL}/bills`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`Bill ${res.data.reference} created`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save bill');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="text-white">{bill ? `Edit Bill ${bill.reference}` : 'New Bill'}</DialogTitle>
          <DialogDescription>Fill in the invoice details for your client.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Client (Invoice To)</Label>
            <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
              <SelectTrigger data-testid="bill-contact-select">
                <SelectValue placeholder="Select a contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((c) => (
                  <SelectItem key={c.contact_id} value={c.contact_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {contacts.length === 0 && (
              <p className="text-xs text-amber-500 mt-1">No contacts yet — add one in the Contacts tab first.</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="bill-due-date" />
            </div>
            <div>
              <Label>Period Start</Label>
              <Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="bill-period-start" />
            </div>
            <div>
              <Label>Period End</Label>
              <Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="bill-period-end" />
            </div>
          </div>

          <div>
            <Label>Line Items</Label>
            <div className="space-y-2 mt-1">
              {form.line_items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    className="flex-1"
                    data-testid={`bill-item-desc-${idx}`}
                  />
                  <Input
                    type="number" placeholder="Qty" value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                    className="w-20"
                    data-testid={`bill-item-qty-${idx}`}
                  />
                  <Input
                    type="number" placeholder="Price $" value={item.price}
                    onChange={(e) => updateItem(idx, 'price', e.target.value)}
                    className="w-28"
                    data-testid={`bill-item-price-${idx}`}
                  />
                  {form.line_items.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeItem(idx)} data-testid={`bill-item-remove-${idx}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItem} data-testid="bill-add-item">
                <Plus className="w-4 h-4 mr-1" /> Add item
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>VAT Amount ($, 0 = not applicable)</Label>
              <Input type="number" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} placeholder="0.00" data-testid="bill-vat" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="bill-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="paid">Paid (adds PAID stamp)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Paid Date</Label>
              <Input
                type="date"
                value={form.paid_date}
                onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                disabled={form.status !== 'paid'}
                data-testid="bill-paid-date"
              />
            </div>
          </div>

          <div>
            <Label>Payment (optional)</Label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <Input placeholder="Method (e.g. Crypto)" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="bill-payment-method" />
              <Input placeholder="Type (e.g. USDC SOL)" value={form.payment_type} onChange={(e) => setForm({ ...form, payment_type: e.target.value })} data-testid="bill-payment-type" />
              <Input placeholder="ID / Tx hash" value={form.payment_ref} onChange={(e) => setForm({ ...form, payment_ref: e.target.value })} data-testid="bill-payment-ref" />
            </div>
            {form.payment_ref && form.payment_ref.length > 12 && (
              <p className="text-xs text-zinc-500 mt-1" data-testid="bill-payment-ref-preview">
                Will display as: <span className="text-zinc-300 font-mono">{form.payment_ref.slice(0, 4)}...{form.payment_ref.slice(-4)}</span>
              </p>
            )}
          </div>

          <div className="flex justify-end gap-6 text-sm border-t border-zinc-800 pt-3">
            <span className="text-zinc-400">Sub-Total: <span className="text-white font-medium">${subtotal.toFixed(2)}</span></span>
            <span className="text-zinc-400">Total: <span className="text-white font-bold">${total.toFixed(2)}</span></span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bill-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {bill ? 'Save Changes' : 'Create Bill'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
